import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getJob, applyToJob } from '../../services/jobs';
import { useAuth } from '../../context/AuthContext';
import type { Job, ApplyToJobRequest } from '../../types/job';
import { JOB_TYPE_LABELS, LOCATION_TYPE_LABELS } from '../../types/job';

export function ApplyJobPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, userType } = useAuth();

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<ApplyToJobRequest>({
    cover_letter: '',
    resume_url: '',
    portfolio_url: '',
    availability: '',
  });

  useEffect(() => {
    // Redirect if not an expert
    if (!isAuthenticated) {
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }
    if (userType !== 'expert') {
      navigate('/jobs');
      return;
    }
  }, [isAuthenticated, userType, navigate]);

  useEffect(() => {
    if (!id) return;

    const loadJob = async () => {
      try {
        const data = await getJob(id);
        if (data.status !== 'active') {
          setError('This job is no longer accepting applications.');
        }
        setJob(data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('Job not found');
        } else {
          setError('Failed to load job details');
        }
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;

    setSubmitting(true);
    setError(null);

    try {
      await applyToJob(job.id, form);
      navigate('/applications', {
        state: { message: 'Application submitted successfully!' },
      });
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to submit application. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const updateForm = (field: keyof ApplyToJobRequest, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse bg-white rounded-lg shadow-sm p-6">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-8" />
            <div className="space-y-4">
              <div className="h-10 bg-gray-200 rounded" />
              <div className="h-32 bg-gray-200 rounded" />
              <div className="h-10 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
            <Link to="/jobs" className="text-blue-600 hover:text-blue-800">
              Back to Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <Link
          to={`/jobs/${job.slug || job.id}`}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to job details
        </Link>

        {/* Job summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Apply for {job.title}</h1>
          <p className="text-gray-600">{job.company_name}</p>
          <div className="flex gap-2 mt-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {JOB_TYPE_LABELS[job.job_type]}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {LOCATION_TYPE_LABELS[job.location_type]}
            </span>
          </div>
        </div>

        {/* Application form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Your Application</h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Cover letter */}
          <div className="mb-6">
            <label htmlFor="cover_letter" className="block text-sm font-medium text-gray-700 mb-2">
              Cover Letter
            </label>
            <textarea
              id="cover_letter"
              rows={8}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Introduce yourself and explain why you're a great fit for this role..."
              value={form.cover_letter || ''}
              onChange={e => updateForm('cover_letter', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              A personalized cover letter significantly increases your chances.
            </p>
          </div>

          {/* Resume URL */}
          <div className="mb-6">
            <label htmlFor="resume_url" className="block text-sm font-medium text-gray-700 mb-2">
              Resume/CV URL
            </label>
            <input
              type="url"
              id="resume_url"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="https://drive.google.com/... or https://linkedin.com/..."
              value={form.resume_url || ''}
              onChange={e => updateForm('resume_url', e.target.value)}
            />
          </div>

          {/* Portfolio URL */}
          <div className="mb-6">
            <label htmlFor="portfolio_url" className="block text-sm font-medium text-gray-700 mb-2">
              Portfolio/GitHub URL (optional)
            </label>
            <input
              type="url"
              id="portfolio_url"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="https://github.com/... or https://yourportfolio.com"
              value={form.portfolio_url || ''}
              onChange={e => updateForm('portfolio_url', e.target.value)}
            />
          </div>

          {/* Availability */}
          <div className="mb-6">
            <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-2">
              Availability
            </label>
            <input
              type="text"
              id="availability"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="e.g., Available immediately, 2 weeks notice, etc."
              value={form.availability || ''}
              onChange={e => updateForm('availability', e.target.value)}
            />
          </div>

          {/* Contract rate (if applicable) */}
          {(job.job_type === 'contract' || job.job_type === 'freelance') && (
            <div className="mb-6">
              <label
                htmlFor="proposed_rate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Proposed Daily Rate (EUR)
              </label>
              <input
                type="number"
                id="proposed_rate"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="e.g., 500"
                value={form.proposed_rate || ''}
                onChange={e =>
                  updateForm('proposed_rate', e.target.value ? parseInt(e.target.value) : undefined)
                }
              />
            </div>
          )}

          {/* Verification notice */}
          {job.require_skill_verification && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">
                    Skill Verification Required
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    This position requires skill verification. Make sure you've completed relevant
                    assessments on your profile.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
            <Link
              to={`/jobs/${job.slug || job.id}`}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
